import { Schedule } from "@mui/icons-material";
import { Typography, Button, Box, TextField, Divider, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { Modal } from "components/common/Modal";
import { useState } from "react";
import { format } from "date-fns";
import { Pickup } from "types/pickup";

interface PickupRescheduleModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  reschedulePickup: (
    pickupUuid: string,
    rescheduleReason: string,
    scheduledAt?: string,
    confirmedTime?: string,
  ) => Promise<void>;
  pickup: Pickup;
}

const timeSlots = [
  "6:00 AM",
  "7:00 AM",
  "8:00 AM",
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
  "6:00 PM",
  "7:00 PM",
];
const rescheduleReasons = ["Client request", "Agent error", "Internal issue", "Other"];

export default function PickupRescheduleModal({ open, setOpen, reschedulePickup, pickup }: PickupRescheduleModalProps) {
  const [rescheduleReason, setRescheduleReason] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");

  const originalScheduledDate = pickup.scheduledAt ? new Date(pickup.scheduledAt) : null;
  const originalRequestedTime = pickup.requestedTime || "";
  const originalConfirmedTime = pickup.confirmedTime || "";

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split("T")[0];

  const handleReschedule = () => {
    reschedulePickup(pickup.uuid, rescheduleReason, selectedDate, selectedTime);
  };

  const isRescheduleDisabled = !rescheduleReason.trim() || Boolean(selectedDate && !selectedTime);

  return (
    <Modal open={open} setOpen={setOpen} width={"700px"}>
      <Box mt={2}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Schedule color="primary" sx={{ fontSize: "48px" }} />
          <Typography mt={1} variant="h5" component="h2" fontWeight={800} fontSize={"20px"}>
            Reschedule Pickup
          </Typography>
        </Box>

        {/* Original Schedule Information */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: "16px" }}>
            Current Schedule
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
            {originalScheduledDate && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Scheduled Date
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {format(originalScheduledDate, "EEEE, MMMM d, yyyy")}
                </Typography>
              </Box>
            )}
            {originalRequestedTime && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Requested Time
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {originalRequestedTime}
                </Typography>
              </Box>
            )}
            {originalConfirmedTime && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Confirmed Time
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {originalConfirmedTime}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, fontSize: "14px" }}>
              Select New Date
            </Typography>
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              fullWidth
              size="small"
              sx={{
                "& input[type='date']::-webkit-calendar-picker-indicator": {
                  position: "absolute",
                  right: "8px",
                  cursor: "pointer",
                },
              }}
              slotProps={{
                htmlInput: {
                  min: today,
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, fontSize: "14px" }}>
              Select New Time
            </Typography>

            {selectedDate ? (
              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel id="time-select-label">Select Time</InputLabel>
                  <Select
                    labelId="time-select-label"
                    value={selectedTime}
                    label="Select Time"
                    onChange={(e) => setSelectedTime(e.target.value)}
                  >
                    {timeSlots.map((timeSlot) => (
                      <MenuItem key={timeSlot} value={timeSlot}>
                        {timeSlot}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "120px",
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                  border: "2px dashed",
                  borderColor: "grey.300",
                }}
              >
                <Typography color="text.secondary" textAlign="center" fontSize="13px">
                  Please select a date first
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel id="reschedule-reason-label">Reason for rescheduling</InputLabel>
            <Select
              labelId="reschedule-reason-label"
              value={rescheduleReason}
              label="Reason for rescheduling"
              onChange={(e) => setRescheduleReason(e.target.value)}
            >
              {rescheduleReasons.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="contained" onClick={handleReschedule} disabled={isRescheduleDisabled}>
            Reschedule Pickup
          </Button>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
